import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { X, FileText } from 'lucide-react'
import { useTranslation } from '@/i18n'
import { computeVisibleCount } from './chatContextFit'

export interface ChatContextChip {
  id: string
  name: string
}

interface ChatContextChipsProps {
  chips: ChatContextChip[]
  onRemove: (id: string) => void
}

function RemoveButton({ id, name, onRemove, className }: { id: string; name: string; onRemove: (id: string) => void; className?: string }) {
  return (
    <button
      type="button"
      onClick={() => onRemove(id)}
      aria-label={`Remove ${name} from chat`}
      className={className}
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <line x1="18" y1="6" x2="6" y2="18" />
        <line x1="6" y1="6" x2="18" y2="18" />
      </svg>
    </button>
  )
}

function Chip({ chip, onRemove }: { chip: ChatContextChip; onRemove: (id: string) => void }) {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-1.5 max-w-[220px] rounded-md border border-[#d4d4d4] dark:border-dark-border bg-[#f4f4f5] dark:bg-dark-card pl-2 pr-1 py-1 text-xs font-medium text-foreground"
      title={chip.name}
    >
      <span className="truncate">{chip.name}</span>
      <RemoveButton
        id={chip.id}
        name={chip.name}
        onRemove={onRemove}
        className="shrink-0 grid place-items-center w-4 h-4 rounded-sm text-muted-foreground hover:text-foreground hover:bg-gray-200 dark:hover:bg-dark-muted transition-colors"
      />
    </span>
  )
}

/**
 * Attached-context chips shown above the chat textarea. The chips are
 * constrained to a SINGLE row (FS-537): an off-screen measurer computes how
 * many fit on the current line, the rest collapse into a "+N more" pill that
 * opens a comfortably-sized modal listing every attached file. The
 * `data-chat-context-chips` hook is preserved (the V2 panel CSS targets it).
 */
export default function ChatContextChips({ chips, onRemove }: ChatContextChipsProps) {
  const { t } = useTranslation()
  const [showAll, setShowAll] = useState(false)
  const [visibleCount, setVisibleCount] = useState(chips.length)
  const measurerRef = useRef<HTMLDivElement>(null)

  // Measure how many chips fit on the first row (reserving a slot for the
  // "+N more" pill when there's overflow). Recompute on resize / chip changes.
  useLayoutEffect(() => {
    const measurer = measurerRef.current
    if (!measurer) return
    const recompute = () => {
      const items = Array.from(measurer.children) as HTMLElement[]
      if (items.length === 0) {
        setVisibleCount(0)
        return
      }
      setVisibleCount(computeVisibleCount(items.map((el) => el.offsetTop)))
    }
    recompute()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(recompute)
    ro.observe(measurer)
    return () => ro.disconnect()
  }, [chips])

  useEffect(() => {
    if (chips.length === 0 && showAll) setShowAll(false)
  }, [chips.length, showAll])

  if (chips.length === 0) return null

  const hidden = Math.max(0, chips.length - visibleCount)

  return (
    <div data-chat-context-chips className="relative flex items-center gap-1.5 mb-1.5 overflow-hidden">
      {/* Off-screen measurer: all chips at full width to detect the first-row fit. */}
      <div
        ref={measurerRef}
        aria-hidden
        className="pointer-events-none invisible absolute left-0 top-0 flex w-full flex-wrap gap-1.5"
      >
        {chips.map((chip) => (
          <Chip key={`measure-${chip.id}`} chip={chip} onRemove={onRemove} />
        ))}
      </div>

      {/* Visible single row. */}
      {chips.slice(0, visibleCount).map((chip) => (
        <Chip key={chip.id} chip={chip} onRemove={onRemove} />
      ))}
      {hidden > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="inline-flex shrink-0 items-center rounded-md border border-[#d4d4d4] dark:border-dark-border bg-[#f4f4f5] dark:bg-dark-card px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-gray-200 dark:hover:bg-dark-muted transition-colors"
        >
          {t('chat:attachments.more', { defaultValue: '+{{count}} more', count: hidden })}
        </button>
      )}

      {showAll && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowAll(false)}
        >
          <div
            role="dialog"
            aria-modal="true"
            className="w-full max-w-lg max-h-[75vh] flex flex-col rounded-2xl bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-dark-border">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('chat:attachments.title', { defaultValue: '{{count}} attached files', count: chips.length })}
              </h2>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                aria-label={t('chat:attachments.close', { defaultValue: 'Close' })}
              >
                <X className="h-5 w-5 text-gray-500 dark:text-dark-text-muted" />
              </button>
            </div>
            {/* Readable vertical list — one file per row, full-width, no cramped chips. */}
            <ul className="flex-1 overflow-y-auto px-3 py-2 divide-y divide-gray-100 dark:divide-dark-border">
              {chips.map((chip) => (
                <li key={`modal-${chip.id}`} className="flex items-center gap-3 px-3 py-2.5">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 min-w-0 truncate text-sm text-foreground" title={chip.name}>
                    {chip.name}
                  </span>
                  <RemoveButton
                    id={chip.id}
                    name={chip.name}
                    onRemove={onRemove}
                    className="shrink-0 grid place-items-center w-6 h-6 rounded-md text-muted-foreground hover:text-foreground hover:bg-gray-100 dark:hover:bg-dark-accent transition-colors"
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  )
}
